import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { AnalyticsOverview } from '../types/billing.ts';
import api from '../services/api.ts';

interface DashboardProps {
  onViewProject: (id: string) => void;
  onViewUser: (id: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onViewProject, onViewUser }) => {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const overviewData = await api.analytics.getOverview();
      setOverview(overviewData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data. Please check if the API server is running.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900 border border-red-700 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-lg font-medium text-red-400">Error Loading Dashboard</h3>
            <p className="text-red-300 mt-1">{error}</p>
            <button
              onClick={loadDashboardData}
              className="mt-3 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-0">Billing Dashboard</h1>
        <div className="text-sm text-gray-400">
          Last updated: {overview ? new Date(overview.timestamp).toLocaleTimeString() : ''}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Cost</p>
              <p className="text-2xl sm:text-3xl font-bold text-white">
                ${overview?.cost_overview.monthly_total.toFixed(2)}
              </p>
              <p className="text-sm text-blue-400">
                ${overview?.cost_overview.total_user_cost.toFixed(2)} user cost
              </p>
            </div>
            <div className="p-3 bg-blue-900 rounded-lg">
              <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Active Users</p>
              <p className="text-2xl sm:text-3xl font-bold text-white">
                {overview?.user_stats.active_users}
              </p>
              <p className="text-sm text-gray-400">
                of {overview?.user_stats.total_users} total
              </p>
            </div>
            <div className="p-3 bg-green-900 rounded-lg">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Active Projects</p>
              <p className="text-2xl sm:text-3xl font-bold text-white">
                {overview?.project_stats.active_projects}
              </p>
              <p className="text-sm text-gray-400">
                of {overview?.project_stats.total_projects} total
              </p>
            </div>
            <div className="p-3 bg-purple-900 rounded-lg">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Credit Utilization</p>
              <p className="text-2xl sm:text-3xl font-bold text-white">
                {overview?.credit_insights.overall_utilization_percentage.toFixed(1)}%
              </p>
              <p className="text-sm text-gray-400">
                {overview?.credit_insights.total_credits_consumed.toFixed(0)} / {overview?.credit_insights.total_credits_allocated.toFixed(0)}
              </p>
            </div>
            <div className="p-3 bg-yellow-900 rounded-lg">
              <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service Distribution */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Service Cost Distribution</h2>
          {overview?.cost_overview.service_breakdown && overview.cost_overview.service_breakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={overview.cost_overview.service_breakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ service, percentage }) => `${service}: ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="cost"
                >
                  {overview.cost_overview.service_breakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any) => [`$${Number(value).toFixed(2)}`, 'Cost']}
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-400">
              No service data available
            </div>
          )}
        </div>

        {/* Monthly Trends */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Monthly Cost Trends</h2>
          {overview?.cost_overview.trends && overview.cost_overview.trends.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={overview.cost_overview.trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  formatter={(value: any) => [`$${Number(value).toFixed(2)}`, 'Cost']}
                />
                <Line type="monotone" dataKey="total_cost" stroke="#3B82F6" strokeWidth={2} name="Total" />
                <Line type="monotone" dataKey="total_user_cost" stroke="#10B981" strokeWidth={2} name="User Cost" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-400">
              No trend data available
            </div>
          )}
        </div>
      </div>

      {/* Top Projects and Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-700">
            <h2 className="text-lg sm:text-xl font-semibold text-white">Top Projects by Cost</h2>
          </div>
          <div className="p-4 sm:p-6">
            {overview?.top_projects && overview.top_projects.length > 0 ? (
              <div className="space-y-4">
                {overview.top_projects.slice(0, 5).map((project, index) => (
                  <div
                    key={project.project_id}
                    className="flex items-center justify-between p-3 sm:p-4 bg-gray-700 rounded-lg hover:bg-gray-600 cursor-pointer transition-colors duration-200"
                    onClick={() => onViewProject(project.project_id)}
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg font-bold text-gray-400">#{index + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white truncate">{project.project_name}</div>
                        <div className="text-sm text-gray-400 truncate">{project.user_email}</div>
                      </div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="text-lg font-bold text-white">${project.total_cost.toFixed(2)}</div>
                      <div className="text-xs text-blue-400">{(project.total_credits || 0).toFixed(0)} credits</div>
                      <div className={`text-xs px-2 py-1 rounded mt-1 ${project.status === 'active' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
                        {project.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                No projects found
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-700">
            <h2 className="text-lg sm:text-xl font-semibold text-white">Top Users by Cost</h2>
          </div>
          <div className="p-4 sm:p-6">
            {overview?.user_stats.top_users_by_cost && overview.user_stats.top_users_by_cost.length > 0 ? (
              <div className="space-y-4">
                {overview.user_stats.top_users_by_cost.slice(0, 5).map((user, index) => (
                  <div
                    key={user.email}
                    className="flex items-center justify-between p-3 sm:p-4 bg-gray-700 rounded-lg hover:bg-gray-600 cursor-pointer transition-colors duration-200"
                    onClick={() => onViewUser(user.email)}
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg font-bold text-gray-400">#{index + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white truncate">{user.name}</div>
                        <div className="text-sm text-gray-400 truncate">{user.email}</div>
                      </div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="text-lg font-bold text-white">${user.monthly_cost.toFixed(2)}</div>
                      <div className="text-xs text-blue-400">{(user.monthly_credits || 0).toFixed(0)} credits</div>
                      <div className="text-xs text-gray-400">monthly cost</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                No users found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;